import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import logoMenu from "../static/media/logoGRMPNG.webp";

// Nueva paleta de colores solicitada
const C = {
  darkBlue: "#0C1F2F", // Azul oscuro para letras y contrastes
  cream: "#F8F6F0", // Fondo crema del navbar
  creamDark: "#E6E2D6", // Crema más oscuro para botones/hover
  white: "#ffffff",
  gold: "#FFCD00", // Se mantiene como acento opcional (ej. borde del logo)
};

const SECTIONS = [
  { path: "/", label: "Inicio" },
  { path: "/tienda", label: "Tienda" },
  { path: "/nosotros", label: "Nosotros" },
  { path: "/contacto", label: "Contacto" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const active = location.pathname;

  const navShadow = useMemo(
    () =>
      scrolled
        ? "0 8px 24px rgba(12, 31, 47, 0.15)"
        : "0 4px 12px rgba(12, 31, 47, 0.08)",
    [scrolled],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
    window.scrollTo(0, 0);
  };

  const linkSx = (isActive) => ({
    px: "16px",
    py: "8px",
    borderRadius: "50px",
    cursor: "pointer",
    fontWeight: isActive ? 700 : 600,
    fontSize: "0.8125rem",
    letterSpacing: "0.01em",
    color: C.darkBlue,
    background: isActive ? C.creamDark : "transparent",
    border: "none",
    transition: "all .22s",
    whiteSpace: "nowrap",
    "&:hover": {
      color: C.darkBlue,
      background: C.creamDark,
    },
  });

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: { xs: "12px", md: "20px" }, // Hace que el navbar flote
        left: "50%",
        transform: "translateX(-50%)",
        width: { xs: "calc(100% - 24px)", md: "calc(100% - 40px)" }, // Márgenes laterales
        maxWidth: "1140px",
        zIndex: 1200,
        background: C.cream,
        boxShadow: navShadow,
        borderRadius: "50px", // Bordes completamente redondeados
        transition: "box-shadow .3s ease, top .3s ease",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          px: { xs: "16px", sm: "24px", md: "28px" },
          height: { xs: "60px", md: "68px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
        }}
      >
        {/* LOGO */}
        <Box
          onClick={() => handleNavigation("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: { xs: "38px", md: "44px" },
              height: { xs: "38px", md: "44px" },
              borderRadius: "50%",
              border: `2.5px solid ${C.creamDark}`,
              background: C.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={logoMenu}
              alt="Eléctrica GRM"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              sx={{
                width: { xs: "28px", md: "34px" },
                height: { xs: "28px", md: "34px" },
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <Typography
              sx={{
                color: C.darkBlue,
                fontWeight: 800,
                fontSize: { xs: "14px", md: "16px" },
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              Eléctrica
            </Typography>
            <Typography
              sx={{
                color: C.darkBlue,
                fontWeight: 700,
                fontSize: "9px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                mt: "2px",
                opacity: 0.8, // Ligero contraste para el subtítulo
              }}
            >
              GRM
            </Typography>
          </Box>
        </Box>

        {/* NAVEGACIÓN DESKTOP */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            justifyContent: "center",
            mx: "28px",
            maxWidth: "580px",
            background: "rgba(12, 31, 47, 0.03)",
            border: `1px solid ${C.creamDark}`,
            borderRadius: "50px",
            p: "4px 6px",
          }}
        >
          {SECTIONS.map((s) => (
            <Box
              key={s.path}
              component="button"
              sx={linkSx(active === s.path)}
              onClick={() => handleNavigation(s.path)}
            >
              {s.label}
            </Box>
          ))}
        </Stack>

        {/* BOTÓN COTIZAR DESKTOP */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={() => handleNavigation("/contacto")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              background: C.darkBlue,
              color: C.cream,
              border: "none",
              borderRadius: "50px",
              px: "20px",
              py: "10px",
              cursor: "pointer",
              transition: "transform .15s, opacity .2s",
              "&:hover": {
                opacity: 0.9,
                transform: "translateY(-1px)",
              },
            }}
          >
            <PhoneIcon sx={{ fontSize: 15 }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                letterSpacing: "0.06em",
              }}
            >
              COTIZAR
            </Typography>
          </Box>
        </Box>

        {/* BOTÓN MENÚ MOBILE */}
        <Box
          onClick={() => setOpen(true)}
          role="button"
          aria-label="Abrir menú"
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: "6px",
            background: C.creamDark,
            border: `1px solid ${C.creamDark}`,
            borderRadius: "50px",
            px: "14px",
            py: "7px",
            cursor: "pointer",
            transition: "opacity .2s",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <Typography
            sx={{
              color: C.darkBlue,
              fontWeight: 800,
              fontSize: "11px",
              letterSpacing: "0.08em",
            }}
          >
            MENÚ
          </Typography>
          <MenuIcon sx={{ color: C.darkBlue, fontSize: 17 }} />
        </Box>
      </Box>

      {/* DRAWER (MENÚ MÓVIL) ADAPTADO */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: "82vw",
            maxWidth: 320,
            height: "100%",
            background: C.cream,
            p: "20px 16px 28px",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "14px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: `2px solid ${C.creamDark}`,
                  background: C.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={logoMenu}
                  alt="Logo"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  sx={{ width: 28, height: 28, objectFit: "contain" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: C.darkBlue,
                    fontWeight: 800,
                    fontSize: "13px",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}
                >
                  Eléctrica
                </Typography>
                <Typography
                  sx={{
                    color: C.darkBlue,
                    fontWeight: 700,
                    fontSize: "9px",
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    mt: "1px",
                    opacity: 0.8,
                  }}
                >
                  GRM
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: C.darkBlue,
                border: `1px solid ${C.creamDark}`,
                borderRadius: "50%",
                width: 32,
                height: 32,
                "&:hover": { background: C.creamDark },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              height: "1px",
              background: C.creamDark,
              mb: "14px",
            }}
          />

          <List sx={{ p: 0, flex: 1 }}>
            {SECTIONS.map((s) => (
              <ListItem key={s.path} disablePadding sx={{ mb: "6px" }}>
                <Box
                  onClick={() => handleNavigation(s.path)}
                  sx={{
                    width: "100%",
                    py: "12px",
                    px: 2.5,
                    borderRadius: "50px",
                    border:
                      active === s.path
                        ? `1px solid ${C.creamDark}`
                        : "1px solid transparent",
                    background: active === s.path ? C.creamDark : "transparent",
                    color: C.darkBlue,
                    fontWeight: active === s.path ? 700 : 600,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    transition: "all .2s",
                    textAlign: "center",
                    "&:hover": {
                      background: C.creamDark,
                    },
                  }}
                >
                  {s.label}
                </Box>
              </ListItem>
            ))}
          </List>

          <Box
            component="button"
            onClick={() => handleNavigation("/contacto")}
            sx={{
              mt: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              background: C.darkBlue,
              color: C.cream,
              border: "none",
              borderRadius: "50px",
              py: "13px",
              cursor: "pointer",
              transition: "opacity .2s",
              "&:hover": { opacity: 0.9 },
            }}
          >
            <PhoneIcon sx={{ fontSize: 16 }} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "13px",
                letterSpacing: "0.06em",
              }}
            >
              COTIZAR AHORA
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
